import {
  BenefitFormData,
  CircuitDayFormData,
} from '@/types/circuits/CircuitDaysFormData';
import { addDays, eachDayOfInterval } from 'date-fns';
import { useCallback, useMemo } from 'react';
import { BenefitType } from '@/types/benefits/benefitTypes';
import { getBenefitTypeName } from '@/utils/benefits/getBenefitTypeName';
import { OptionalBenefitComputationFn } from '@/components/estimates/EstimateDetailsAndOptions';

interface EstimateDayStepCardProps {
  step: CircuitDayFormData['steps'][number]['transport'];
  date: Date;
  dayIndex: number;
  computeOptionalBenefitPrice: OptionalBenefitComputationFn;
}

export const useEstimatePrices = ({
  step,
  date,
  dayIndex,
  computeOptionalBenefitPrice,
}: EstimateDayStepCardProps) => {
  const type: BenefitType | null = useMemo(
    () =>
      step.standardBenefit
        ? getBenefitTypeName(
            step.standardBenefit.type === 'ACTIVITY'
              ? (step.standardBenefit.activity?.activityType ??
                  step.standardBenefit.type)
              : step.standardBenefit.type,
          )
        : step.optionalBenefits.length > 0
          ? getBenefitTypeName(
              step.optionalBenefits[0].type === 'ACTIVITY'
                ? (step.optionalBenefits[0].activity?.activityType ??
                    step.optionalBenefits[0].type)
                : step.optionalBenefits[0].type,
            )
          : null,
    [step.optionalBenefits, step.standardBenefit],
  );

  const stepDates = useMemo(() => {
    if (!step.nbDays || step.nbDays <= 1) return null;
    return {
      startDate: date,
      endDate: addDays(date, step.nbDays - 1),
    };
  }, [date, step.nbDays]);

  const allDates = useMemo(
    () =>
      stepDates
        ? eachDayOfInterval({
            start: stepDates.startDate,
            end: stepDates.endDate,
          }).map((d) => d)
        : [date],
    [date, stepDates],
  );

  const standardBenefitPrice = useMemo(() => {
    const computedPrice = allDates
      .map((d, dateIndex) =>
        step.standardBenefit
          ? computeOptionalBenefitPrice(
              step.standardBenefit,
              d,
              dayIndex + dateIndex,
              step.nbDays,
              undefined,
              undefined,
              undefined,
              step.standardBenefitPensionType ??
                step.standardBenefit.hotel?.pricingPensionType ??
                undefined,
            )
          : 0,
      )
      .reduce((a, b) => a + b, 0);

    return computedPrice;
  }, [
    allDates,
    computeOptionalBenefitPrice,
    dayIndex,
    step.nbDays,
    step.standardBenefit,
  ]);

  const computeBenefitPriceDelta = useCallback(
    (optionalBenefit: BenefitFormData) => {
      const selectedHotelPensionType =
        step.optionalBenefitsPensionTypes?.[optionalBenefit.id] ??
        optionalBenefit.hotel?.pricingPensionType ??
        undefined;
      return (
        allDates
          .map((d, dateIndex) =>
            computeOptionalBenefitPrice(
              optionalBenefit,
              d,
              dayIndex + dateIndex,
              step.nbDays,
              undefined,
              undefined,
              undefined,
              selectedHotelPensionType,
            ),
          )
          .reduce((a, b) => a + b, 0) - standardBenefitPrice
      );
    },
    [
      allDates,
      computeOptionalBenefitPrice,
      dayIndex,
      standardBenefitPrice,
      step.nbDays,
      step.optionalBenefitsPensionTypes,
    ],
  );

  const computeHotelRoomPriceForAllDates = useCallback(
    (
      benefit: BenefitFormData,
      nbRoomAdults: number,
      nbRoomChildren: number,
      nbRoomAdolescents: number,
      selectedHotelPensionType?: string,
    ) =>
      allDates
        .map((d, dateIndex) =>
          computeOptionalBenefitPrice(
            benefit,
            d,
            dayIndex + dateIndex,
            step.nbDays,
            nbRoomAdults,
            nbRoomAdolescents,
            nbRoomChildren,
            selectedHotelPensionType,
          ),
        )
        .reduce((a, b) => a + b, 0),
    [allDates, computeOptionalBenefitPrice, dayIndex, step.nbDays],
  );

  return {
    type,
    computeBenefitPriceDelta,
    stepDates,
    allDates,
    standardBenefitPrice,
    computeHotelRoomPriceForAllDates,
  };
};
